import { findBottleSkin, type PublicRoomState } from '@spin/shared';
import { useEffect, useId, useRef } from 'react';
import { serverNow } from '../store/room';
import { BOTTLE_CENTER, bottleRotation } from './layout';

/**
 * The bottle artwork (original SVG). The neck end points at the chosen player. Skins
 * recolour it or switch the shape (flask, baby bottle); see BOTTLE_SKINS in @spin/shared.
 */
export function BottleArt({ className, skin }: { className?: string; skin?: string }) {
  const id = useId().replace(/:/g, '');
  const s = findBottleSkin(skin);
  const [edge, body, light] = s.glass;
  const glass = `url(#${id}-glass)`;
  const markSize = s.mark.length > 2 ? 8 : 12;
  return (
    <svg viewBox="0 0 48 150" className={className} aria-hidden>
      <defs>
        <linearGradient id={`${id}-glass`} x1="0" x2="1">
          <stop offset="0" stopColor={edge} />
          <stop offset="0.3" stopColor={body} />
          <stop offset="0.48" stopColor={light} />
          <stop offset="0.68" stopColor={body} />
          <stop offset="1" stopColor={edge} />
        </linearGradient>
        {/* Shade across a round surface: darker edges, a bright stripe left of the middle. */}
        <linearGradient id={`${id}-round`} x1="0" x2="1">
          <stop offset="0" stopColor="#000000" stopOpacity="0.3" />
          <stop offset="0.35" stopColor="#ffffff" stopOpacity="0.25" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.32" />
        </linearGradient>
      </defs>
      {s.shape === 'flask' ? (
        <>
          <rect x="18" y="9" width="12" height="11" rx="3" fill={s.foil} />
          <path d="M20 20 H28 V82 Q46 92 46 116 A22 22 0 0 1 2 116 Q2 92 20 82 Z" fill={glass} />
          <path d="M5 116 A19 19 0 0 0 43 116 Z" fill={light} opacity="0.35" />
          <circle cx="16" cy="122" r="3" fill="#ffffff" opacity="0.5" />
          <circle cx="29" cy="128" r="2" fill="#ffffff" opacity="0.45" />
          <text x="24" y="112" textAnchor="middle" fontSize={markSize} fontWeight="900" fill={s.markColor}>
            {s.mark}
          </text>
          <rect x="22" y="24" width="2" height="54" rx="1" fill="rgba(255,255,255,0.5)" />
        </>
      ) : s.shape === 'baby' ? (
        <>
          <path d="M18 30 Q18 12 24 12 Q30 12 30 30 Z" fill="#f4d3b5" />
          <rect x="10" y="29" width="28" height="13" rx="4" fill={s.foil} />
          <rect x="8" y="42" width="32" height="102" rx="10" fill={glass} />
          {[60, 76, 92, 108, 124].map((y) => (
            <rect key={y} x="30" y={y} width="6" height="2" rx="1" fill={s.foil} opacity="0.8" />
          ))}
          <path
            d="M20 98 C14 94 15 88 18.5 88.5 C19.7 88.7 20.5 90 20.5 90 C20.5 90 21.3 88.7 22.5 88.5 C26 88 27 94 20 98 Z"
            fill="#ff7ab0"
          />
          <rect x="12" y="48" width="3" height="88" rx="1.5" fill="rgba(255,255,255,0.7)" />
        </>
      ) : (
        <>
          <path
            d="M18 13 Q18 10 21 10 H27 Q30 10 30 13 V45 Q30 53 38 61 Q44 67 44 77 V138 Q44 146 36 146 H12 Q4 146 4 138 V77 Q4 67 10 61 Q18 53 18 45 Z"
            fill={glass}
            stroke={edge}
            strokeWidth="1.2"
          />
          <path d="M17.6 13 Q17.6 9.6 21 9.6 H27 Q30.4 9.6 30.4 13 V36 H17.6 Z" fill={s.foil} />
          <rect x="17.2" y="35" width="13.6" height="3.4" rx="1.2" fill={s.foil} opacity="0.85" />
          <path
            d="M17.6 13 Q17.6 9.6 21 9.6 H27 Q30.4 9.6 30.4 13 V38.4 H17.6 Z"
            fill={`url(#${id}-round)`}
          />
          {s.label && (
            <>
              <rect x="4" y="88" width="40" height="31" fill={s.label} />
              <rect x="4" y="88" width="40" height="31" fill={`url(#${id}-round)`} />
              <text
                x="24"
                y="108"
                textAnchor="middle"
                fontSize={markSize}
                fontWeight="900"
                fill={s.markColor}
              >
                {s.mark}
              </text>
            </>
          )}
          <rect x="9" y="66" width="4" height="70" rx="2" fill="rgba(255,255,255,0.4)" />
          <rect x="10" y="70" width="1.6" height="18" rx="0.8" fill="rgba(255,255,255,0.85)" />
          <ellipse cx="24" cy="141" rx="16" ry="3" fill="rgba(0,0,0,0.18)" />
          <rect x="20.5" y="14" width="2" height="19" rx="1" fill="rgba(255,255,255,0.55)" />
        </>
      )}
    </svg>
  );
}

/**
 * The bottle on the table. It rotates to the absolute angle chosen by the server: during a spin it animates over
 * the remaining spin time; on a snapshot (reconnect, late join) it snaps to the resting angle.
 */
export function Bottle({
  room,
  canSpin,
  onSpin,
}: {
  room: PublicRoomState;
  canSpin: boolean;
  onSpin: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const phase = room.phase;
  const spinning = phase.kind === 'SPINNING';
  const target = spinning ? phase.angle : room.bottleAngle;
  const endsAt = spinning ? phase.endsAt : 0;
  const capacity = room.capacity;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const remaining = endsAt - serverNow();
    if (spinning && remaining > 150) {
      el.style.transition = `transform ${remaining}ms cubic-bezier(0.12, 0.62, 0.18, 1)`;
    } else {
      el.style.transition = 'none';
    }
    el.style.transform = `rotate(${bottleRotation(target, capacity)}deg)`;
  }, [target, spinning, endsAt, capacity]);

  return (
    <button
      ref={ref}
      className={`bottle ${canSpin ? 'can-spin' : ''} ${spinning ? 'spinning' : ''}`}
      style={{ left: `${BOTTLE_CENTER.left}%`, top: `${BOTTLE_CENTER.top}%` }}
      onClick={canSpin ? onSpin : undefined}
      disabled={!canSpin}
      aria-label="bottle"
      data-testid="bottle"
    >
      <BottleArt skin={room.bottleSkin} />
    </button>
  );
}
